import React, { useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  TextField,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import {
  AutoAwesome as KundaliIcon,
  EventAvailable as EventAvailableIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import { KundaliData, KundaliRequest, PlanetData, kundaliApi } from '../services/api';
import PlacesAutocomplete from '../components/PlacesAutocomplete';

const MAROON = '#7B2D26';
const GOLD = '#C8922A';
const CELL_SIZE = 100;
const GRID_SIZE = 4 * CELL_SIZE;
const HOURS = Array.from({ length: 12 }, (_, index) => String(index + 1));
const MINUTES = Array.from({ length: 12 }, (_, index) => String(index * 5).padStart(2, '0'));
const MERIDIEMS = ['AM', 'PM'] as const;
const SIGN_NAMES = ['Aries', 'Taurus', 'Gemini', 'Cancer', 'Leo', 'Virgo', 'Libra', 'Scorpio', 'Sagitt.', 'Capri.', 'Aquar.', 'Pisces'];
const SIGN_NAMES_TELUGU = ['మేషం', 'వృషభం', 'మిథునం', 'కర్కాటకం', 'సింహం', 'కన్య', 'తుల', 'వృశ్చికం', 'ధనుస్సు', 'మకరం', 'కుంభం', 'మీనం'];
const DIVISIONAL_CHART_ORDER = ['D1', 'D2', 'D3', 'D4', 'D6', 'D7', 'D8', 'D9', 'D10', 'D12', 'D16', 'D20', 'D24', 'D27', 'D30', 'D40', 'D45', 'D60'];

const SIGN_POSITIONS: Record<number, { row: number; col: number }> = {
  0: { row: 0, col: 1 },
  1: { row: 0, col: 2 },
  2: { row: 0, col: 3 },
  3: { row: 1, col: 3 },
  4: { row: 2, col: 3 },
  5: { row: 3, col: 3 },
  6: { row: 3, col: 2 },
  7: { row: 3, col: 1 },
  8: { row: 3, col: 0 },
  9: { row: 2, col: 0 },
  10: { row: 1, col: 0 },
  11: { row: 0, col: 0 },
};

const PLANET_ABBREV: Record<string, string> = {
  Sun: 'Su',
  Moon: 'Mo',
  Mars: 'Ma',
  Mercury: 'Me',
  Jupiter: 'Ju',
  Venus: 'Ve',
  Saturn: 'Sa',
  Rahu: 'Ra',
  Ketu: 'Ke',
  Ascendant: 'Asc',
  Lagna: 'Asc',
};

const cardSx = {
  p: { xs: 2.5, md: 3 },
  background: 'linear-gradient(135deg, rgba(255,255,255,0.86), rgba(255,245,235,0.78))',
  backdropFilter: 'blur(18px)',
  border: '1px solid rgba(200,146,42,0.18)',
  boxShadow: '0 12px 32px rgba(123,45,38,0.08)',
};

const tableRowSx = {
  '&:nth-of-type(even)': {
    backgroundColor: 'rgba(200,146,42,0.08)',
  },
  '&:hover': {
    backgroundColor: 'rgba(123,45,38,0.06)',
  },
};

const to24HourTime = (hour: string, minute: string, meridiem: (typeof MERIDIEMS)[number]) => {
  let parsedHour = Number(hour);
  if (meridiem === 'AM') {
    parsedHour = parsedHour === 12 ? 0 : parsedHour;
  } else {
    parsedHour = parsedHour === 12 ? 12 : parsedHour + 12;
  }

  return `${String(parsedHour).padStart(2, '0')}:${minute}`;
};

const formatDisplayDate = (value?: string) => (value ? dayjs(value).format('DD MMM YYYY') : '—');
const formatDisplayDateTime = (value?: string) => (value ? dayjs(value).format('DD MMM YYYY, hh:mm A') : '—');
const formatDisplayTime = (value?: string) => (value ? dayjs(`2000-01-01T${value}`).format('hh:mm A') : '—');
const formatDegree = (value: number) => `${value.toFixed(2)}°`;
const formatDuration = (value: number) => `${Number.isInteger(value) ? value.toFixed(0) : value.toFixed(2)} years`;
const formatShadbalaValue = (value: number | null) => (value == null ? '—' : value.toFixed(2));
const getPlanetAbbreviation = (planet: string) => {
  if (/asc|lagna/i.test(planet)) return 'Asc';
  return PLANET_ABBREV[planet] || planet.slice(0, 2);
};

interface SouthIndianChartProps {
  kundali: KundaliData;
}

const SouthIndianChart: React.FC<SouthIndianChartProps> = ({ kundali }) => {
  const planetsBySign = useMemo(() => {
    const grouped = new Map<number, PlanetData[]>();
    kundali.planets.forEach((planet) => {
      const items = grouped.get(planet.sign_index) || [];
      items.push(planet);
      grouped.set(planet.sign_index, items);
    });
    return grouped;
  }, [kundali.planets]);

  return (
    <Box sx={{ width: '100%', maxWidth: { xs: 320, sm: 400, md: 440 }, mx: 'auto', p: { xs: 0.5, md: 2 } }}>
      <svg viewBox={`0 0 ${GRID_SIZE} ${GRID_SIZE}`} style={{ width: '100%', height: 'auto', display: 'block' }} role="img" aria-label="South Indian Rasi chart">
        <rect x={0} y={0} width={GRID_SIZE} height={GRID_SIZE} fill="#FFFDF9" stroke={MAROON} strokeWidth={4} rx={16} ry={16} />
        <rect x={CELL_SIZE} y={CELL_SIZE} width={CELL_SIZE * 2} height={CELL_SIZE * 2} fill="#FFF8EF" stroke={MAROON} strokeWidth={2.5} rx={12} ry={12} />

        {Object.entries(SIGN_POSITIONS).map(([signIndexKey, position]) => {
          const signIndex = Number(signIndexKey);
          const x = position.col * CELL_SIZE;
          const y = position.row * CELL_SIZE;
          const planets = planetsBySign.get(signIndex) || [];
          const isAscendant = kundali.ascendant.sign_index === signIndex;

          return (
            <g key={signIndex}>
              <rect
                x={x}
                y={y}
                width={CELL_SIZE}
                height={CELL_SIZE}
                fill={isAscendant ? 'rgba(200,146,42,0.22)' : '#FFFEFB'}
                stroke={isAscendant ? GOLD : MAROON}
                strokeWidth={isAscendant ? 3 : 2}
              />
              <text x={x + 8} y={y + 14} fill={MAROON} fontSize="10" fontWeight="700">
                {SIGN_NAMES[signIndex]}
              </text>
              <text x={x + 8} y={y + 26} fill="#8B4513" fontSize="9" fontFamily="'Noto Sans Telugu', sans-serif">
                {SIGN_NAMES_TELUGU[signIndex]}
              </text>
              {isAscendant && (
                <text x={x + CELL_SIZE - 8} y={y + 18} fill={GOLD} fontSize="11" fontWeight="700" textAnchor="end">
                  Asc
                </text>
              )}
              {planets.map((planet, index) => {
                const columnIndex = Math.floor(index / 3);
                const rowIndex = index % 3;
                return (
                  <text
                    key={`${planet.name}-${planet.degree}`}
                    x={x + 10 + columnIndex * 42}
                    y={y + 44 + rowIndex * 16}
                    fill="#3D2A24"
                    fontSize="10"
                    fontWeight="600"
                  >
                    {`${getPlanetAbbreviation(planet.name)} ${planet.degree.toFixed(1)}°`}
                  </text>
                );
              })}
            </g>
          );
        })}

        <text x={GRID_SIZE / 2} y={GRID_SIZE / 2 - 16} fill={MAROON} fontSize="16" fontWeight="700" textAnchor="middle">
          {kundali.birth_details.name || 'Your'}
        </text>
        <text x={GRID_SIZE / 2} y={GRID_SIZE / 2 + 8} fill={GOLD} fontSize="18" fontWeight="700" textAnchor="middle">
          Rasi Chart
        </text>
        <text x={GRID_SIZE / 2} y={GRID_SIZE / 2 + 28} fill={MAROON} fontSize="14" fontWeight="600" textAnchor="middle" fontFamily="'Noto Sans Telugu', sans-serif">
          రాశి చక్రం
        </text>
      </svg>
    </Box>
  );
};

interface DivisionalChartGridProps {
  chartName: string;
  chartNameTelugu: string;
  entries: KundaliData['divisional_charts'][string]['planets'];
}

const DivisionalChartGrid: React.FC<DivisionalChartGridProps> = ({ chartName, chartNameTelugu, entries }) => {
  const planetsBySign = useMemo(() => {
    const grouped = new Map<number, typeof entries>();
    entries.forEach((entry) => {
      const items = grouped.get(entry.sign_index) || [];
      items.push(entry);
      grouped.set(entry.sign_index, items);
    });
    return grouped;
  }, [entries]);

  const signIndexByCell = useMemo(() => {
    const mapping = new Map<number, number>();
    Object.entries(SIGN_POSITIONS).forEach(([signIndexKey, position]) => {
      mapping.set(position.row * 4 + position.col, Number(signIndexKey));
    });
    return mapping;
  }, []);

  const ascendantSignIndex = useMemo(
    () => entries.find((entry) => /asc|lagna/i.test(entry.name))?.sign_index,
    [entries]
  );

  return (
    <Box sx={{ width: '100%', maxWidth: 520, mx: 'auto' }}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
          gap: 1,
          p: 1,
          borderRadius: 3,
          backgroundColor: '#FFF8EF',
          border: '2px solid rgba(123,45,38,0.18)',
        }}
      >
        {Array.from({ length: 16 }, (_, cellIndex) => {
          const signIndex = signIndexByCell.get(cellIndex);

          if (signIndex == null) {
            const isCenterCell = cellIndex === 5;
            return (
              <Box
                key={cellIndex}
                sx={{
                  minHeight: { xs: 72, sm: 92 },
                  borderRadius: 2,
                  background: isCenterCell ? 'linear-gradient(135deg, rgba(123,45,38,0.12), rgba(200,146,42,0.14))' : 'rgba(255,255,255,0.72)',
                  border: isCenterCell ? `1px solid ${GOLD}` : '1px dashed rgba(123,45,38,0.12)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  textAlign: 'center',
                  px: 1,
                }}
              >
                {isCenterCell ? (
                  <Box>
                    <Typography sx={{ fontWeight: 700, color: MAROON, fontSize: { xs: '0.85rem', sm: '0.95rem' } }}>{chartName}</Typography>
                    <Typography sx={{ color: GOLD, fontWeight: 700, fontSize: { xs: '0.8rem', sm: '0.9rem' }, fontFamily: "'Noto Sans Telugu', sans-serif" }}>
                      {chartNameTelugu}
                    </Typography>
                  </Box>
                ) : null}
              </Box>
            );
          }

          const signPlanets = planetsBySign.get(signIndex) || [];
          const isAscendant = ascendantSignIndex === signIndex;

          return (
            <Box
              key={signIndex}
              sx={{
                minHeight: { xs: 72, sm: 92 },
                borderRadius: 2,
                backgroundColor: isAscendant ? 'rgba(200,146,42,0.20)' : 'rgba(255,255,255,0.9)',
                border: `1px solid ${isAscendant ? GOLD : 'rgba(123,45,38,0.18)'}`,
                p: 1,
                display: 'flex',
                flexDirection: 'column',
                gap: 0.4,
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 0.5 }}>
                <Typography sx={{ fontSize: { xs: '0.68rem', sm: '0.74rem' }, fontWeight: 700, color: MAROON }}>
                  {SIGN_NAMES[signIndex]}
                </Typography>
                {isAscendant && <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: GOLD }}>Asc</Typography>}
              </Box>
              <Typography sx={{ fontSize: { xs: '0.62rem', sm: '0.7rem' }, color: '#8B4513', fontFamily: "'Noto Sans Telugu', sans-serif" }}>
                {SIGN_NAMES_TELUGU[signIndex]}
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 'auto' }}>
                {signPlanets.map((entry) => (
                  <Chip
                    key={`${entry.name}-${entry.sign}-${chartName}`}
                    label={getPlanetAbbreviation(entry.name)}
                    size="small"
                    sx={{
                      height: 22,
                      backgroundColor: /asc|lagna/i.test(entry.name) ? 'rgba(200,146,42,0.18)' : 'rgba(123,45,38,0.08)',
                      color: MAROON,
                      fontWeight: 600,
                      '& .MuiChip-label': { px: 0.8 },
                    }}
                  />
                ))}
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

const Kundali: React.FC = () => {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState<Dayjs | null>(null);
  const [hour, setHour] = useState('6');
  const [minute, setMinute] = useState('00');
  const [meridiem, setMeridiem] = useState<(typeof MERIDIEMS)[number]>('AM');
  const [placeOfBirth, setPlaceOfBirth] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [kundali, setKundali] = useState<KundaliData | null>(null);
  const [lastRequest, setLastRequest] = useState<KundaliRequest | null>(null);
  const [selectedChart, setSelectedChart] = useState('D9');
  const [expandedDasha, setExpandedDasha] = useState<string | false>(false);

  const canSubmit = Boolean(name.trim() && dateOfBirth && placeOfBirth.trim());

  const availableDivisionalCharts = useMemo(() => {
    if (!kundali?.divisional_charts) return [] as string[];

    const availableKeys = Object.keys(kundali.divisional_charts);
    const ordered = DIVISIONAL_CHART_ORDER.filter((key) => availableKeys.includes(key));
    const extras = availableKeys.filter((key) => !DIVISIONAL_CHART_ORDER.includes(key));
    return [...ordered, ...extras];
  }, [kundali]);

  const activeChartKey = useMemo(() => {
    if (!availableDivisionalCharts.length) return selectedChart;
    return availableDivisionalCharts.includes(selectedChart) ? selectedChart : availableDivisionalCharts[0];
  }, [availableDivisionalCharts, selectedChart]);

  const activeDivisionalChart = kundali?.divisional_charts?.[activeChartKey];

  const shadbalaRows = useMemo(() => {
    if (!kundali?.shadbala?.planets) return [] as Array<KundaliData['shadbala']['planets'][string] & { planet: string; planet_telugu: string }>;

    const teluguByPlanet = new Map(kundali.planets.map((planet) => [planet.name, planet.name_telugu]));
    const orderedPlanetNames = Array.from(
      new Set([...kundali.planets.map((planet) => planet.name), ...Object.keys(kundali.shadbala.planets)])
    ).filter((planetName) => kundali.shadbala.planets[planetName]);

    return orderedPlanetNames.map((planetName) => ({
      planet: planetName,
      planet_telugu: teluguByPlanet.get(planetName) || '',
      ...kundali.shadbala.planets[planetName],
    }));
  }, [kundali]);

  const maxShadbalaValue = useMemo(() => {
    if (!shadbalaRows.length) return 1;
    const value = Math.max(
      ...shadbalaRows.flatMap((entry) => [entry.total, entry.required_minimum ?? 0])
    );
    return value > 0 ? value * 1.15 : 1;
  }, [shadbalaRows]);

  const handleGenerate = async () => {
    if (!dateOfBirth || !canSubmit) {
      setError('Please fill in all birth details before generating the chart.');
      return;
    }

    const payload: KundaliRequest = {
      name: name.trim(),
      date_of_birth: dateOfBirth.format('YYYY-MM-DD'),
      time_of_birth: to24HourTime(hour, minute, meridiem),
      place_of_birth: placeOfBirth.trim(),
    };

    try {
      setLoading(true);
      setError(null);
      const response = await kundaliApi.generate(payload);
      setKundali(response);
      setLastRequest(payload);
      setSelectedChart(response.divisional_charts?.D9 ? 'D9' : Object.keys(response.divisional_charts || {})[0] || 'D9');
      setExpandedDasha(response.dasha.current_mahadasha || false);
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.response?.data?.message || 'Unable to generate kundali right now. Please try again shortly.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 3, md: 5 }, px: { xs: 2, sm: 3 } }}>
      <Box sx={{ textAlign: 'center', mb: { xs: 4, md: 5 }, pt: { xs: 2, md: 3 } }}>
        <Typography variant="h1" sx={{ mb: 1 }}>
          Kundali Generator / కుండలి
        </Typography>
        <Typography variant="h5" sx={{ color: 'text.secondary', maxWidth: 680, mx: 'auto', fontSize: { xs: '0.95rem', md: '1.25rem' } }}>
          Generate your Vedic birth chart with planetary positions, nakshatras, dasha timeline, and South Indian Rasi chart — in English &amp; Telugu.
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Card sx={{ ...cardSx, mb: 4 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
            <KundaliIcon sx={{ color: 'secondary.main' }} />
            <Typography variant="h4">Birth Details</Typography>
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2.5 }}>
            <TextField label="Name" value={name} onChange={(event) => setName(event.target.value)} fullWidth />
            <DatePicker
              label="Date of Birth"
              value={dateOfBirth}
              onChange={setDateOfBirth}
              slotProps={{ textField: { fullWidth: true } }}
            />
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 1.5 }}>
              <TextField select label="Hour" value={hour} onChange={(event) => setHour(event.target.value)} fullWidth>
                {HOURS.map((item) => (
                  <MenuItem key={item} value={item}>
                    {item}
                  </MenuItem>
                ))}
              </TextField>
              <TextField select label="Minute" value={minute} onChange={(event) => setMinute(event.target.value)} fullWidth>
                {MINUTES.map((item) => (
                  <MenuItem key={item} value={item}>
                    {item}
                  </MenuItem>
                ))}
              </TextField>
              <TextField select label="AM/PM" value={meridiem} onChange={(event) => setMeridiem(event.target.value as (typeof MERIDIEMS)[number])} fullWidth>
                {MERIDIEMS.map((item) => (
                  <MenuItem key={item} value={item}>
                    {item}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
            <PlacesAutocomplete
              value={placeOfBirth}
              onChange={setPlaceOfBirth}
            />
          </Box>

          <Box sx={{ mt: 3, display: 'flex', justifyContent: { xs: 'stretch', sm: 'flex-end' } }}>
            <Button variant="contained" size="large" onClick={handleGenerate} disabled={!canSubmit || loading} fullWidth sx={{ maxWidth: { sm: 260 } }}>
              {loading ? <><CircularProgress size={20} color="inherit" sx={{ mr: 1 }} /> Generating...</> : 'Generate Chart'}
            </Button>
          </Box>
        </CardContent>
      </Card>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
          <CircularProgress size={52} />
        </Box>
      )}

      {kundali && !loading && (
        <Box sx={{ display: 'grid', gap: 3.5 }}>
          <Card sx={cardSx}>
            <CardContent>
              <Typography variant="h4" sx={{ mb: 2.5 }}>
                Birth Details / జన్మ వివరాలు
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(3, minmax(0, 1fr))' }, gap: 2 }}>
                {[
                  ['Name', kundali.birth_details.name],
                  ['Date of Birth', formatDisplayDate(kundali.birth_details.date_of_birth)],
                  ['Time of Birth', formatDisplayTime(kundali.birth_details.time_of_birth)],
                  ['Place', kundali.birth_details.place_of_birth],
                  ['Coordinates', `${kundali.birth_details.latitude.toFixed(4)}, ${kundali.birth_details.longitude.toFixed(4)}`],
                  ['Timezone', kundali.birth_details.timezone],
                  ['UTC Datetime', formatDisplayDateTime(kundali.birth_details.utc_datetime)],
                  ['Ascendant / లగ్నం', `${kundali.ascendant.sign} (${kundali.ascendant.sign_telugu || kundali.ascendant.sign_sanskrit}) ${formatDegree(kundali.ascendant.degree)}`],
                ].map(([label, value]) => (
                  <Box key={label} sx={{ p: 1.5, borderRadius: 2, backgroundColor: 'rgba(255,255,255,0.72)', border: '1px solid rgba(123,45,38,0.08)' }}>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>{label}</Typography>
                    <Typography variant="body1" sx={{ color: 'text.primary', fontWeight: 600 }}>{value}</Typography>
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>

          <Card sx={cardSx}>
            <CardContent>
              <Typography variant="h4" sx={{ mb: 0.5, textAlign: 'center' }}>
                South Indian Rasi Chart / రాశి చక్రం
              </Typography>
              <Typography variant="body2" sx={{ textAlign: 'center', mb: 2.5 }}>
                Fixed sign layout with planetary placements and ascendant highlight.
              </Typography>
              <SouthIndianChart kundali={kundali} />
            </CardContent>
          </Card>

          <Card sx={cardSx}>
            <CardContent>
              <Typography variant="h4" sx={{ mb: 2.5 }}>
                Planet Positions / గ్రహ స్థానాలు
              </Typography>
              <TableContainer sx={{ overflowX: 'auto', mx: { xs: -1, md: 0 } }}>
                <Table size="small" sx={{ minWidth: 600 }}>
                  <TableHead>
                    <TableRow>
                      {['Planet / గ్రహం', 'Sign / రాశి', 'Degree', 'Nakshatra / నక్షత్రం', 'Lord / అధిపతి', 'House', 'Retro'].map((header) => (
                        <TableCell key={header} sx={{ color: 'primary.main', fontWeight: 700, fontSize: { xs: '0.7rem', md: '0.8rem' }, whiteSpace: 'nowrap' }}>
                          {header}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {kundali.planets.map((planet) => (
                      <TableRow key={`${planet.name}-${planet.total_degree}`} sx={tableRowSx}>
                        <TableCell>
                          <Typography sx={{ fontWeight: 600, fontSize: '0.85rem' }}>{planet.name}</Typography>
                          <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>{planet.name_telugu}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography sx={{ fontSize: '0.85rem' }}>{planet.sign}</Typography>
                          <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>{planet.sign_telugu}</Typography>
                        </TableCell>
                        <TableCell>{formatDegree(planet.degree)}</TableCell>
                        <TableCell>
                          <Typography sx={{ fontSize: '0.85rem' }}>{`${planet.nakshatra} (P${planet.nakshatra_pada})`}</Typography>
                          <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>{planet.nakshatra_telugu}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography sx={{ fontSize: '0.85rem' }}>{planet.nakshatra_lord}</Typography>
                          <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>{planet.nakshatra_lord_telugu}</Typography>
                        </TableCell>
                        <TableCell>{planet.house}</TableCell>
                        <TableCell>
                          {planet.is_retrograde ? <Chip label="R" size="small" color="secondary" /> : '—'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>

          <Card sx={cardSx}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, gap: 1.5, flexDirection: { xs: 'column', sm: 'row' }, mb: 2.5 }}>
                <Box>
                  <Typography variant="h4">Vimshottari Dasha / విమ్శోత్తరి దశ</Typography>
                  <Typography variant="body2">Current Mahadasha ends on {formatDisplayDate(kundali.dasha.current_mahadasha_end)}</Typography>
                </Box>
                <Chip label={`Current: ${kundali.dasha.current_mahadasha}`} color="primary" />
              </Box>
              <TableContainer sx={{ overflowX: 'auto', mx: { xs: -1, md: 0 } }}>
                <Table size="small" sx={{ minWidth: 500 }}>
                  <TableHead>
                    <TableRow>
                      {['Planet / గ్రహం', 'Start Date', 'End Date', 'Duration', 'Status'].map((header) => (
                        <TableCell key={header} sx={{ color: 'primary.main', fontWeight: 700, whiteSpace: 'nowrap' }}>
                          {header}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {kundali.dasha.periods.map((period) => (
                      <TableRow key={`${period.planet}-${period.start_date}`} sx={tableRowSx}>
                        <TableCell>
                          <Typography sx={{ fontWeight: 600, fontSize: '0.85rem' }}>{period.planet}</Typography>
                          <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>{period.planet_telugu}</Typography>
                        </TableCell>
                        <TableCell>{formatDisplayDate(period.start_date)}</TableCell>
                        <TableCell>{formatDisplayDate(period.end_date)}</TableCell>
                        <TableCell>{formatDuration(period.duration_years)}</TableCell>
                        <TableCell>
                          {period.is_current ? <Chip label="Current Mahadasha" size="small" color="secondary" /> : '—'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>

          <Card sx={cardSx}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, gap: 1.5, flexDirection: { xs: 'column', sm: 'row' }, mb: 2.5 }}>
                <Box>
                  <Typography variant="h4">Antardasha / అంతర్దశ</Typography>
                  <Typography variant="body2">Sub-period timeline for each Mahadasha with current Bhukti highlight.</Typography>
                </Box>
                <Chip
                  label={`Current: ${kundali.dasha.current_mahadasha} - ${kundali.dasha.current_antardasha}`}
                  sx={{ backgroundColor: 'rgba(200,146,42,0.18)', color: MAROON, fontWeight: 700 }}
                />
              </Box>

              {kundali.dasha.periods.map((period) => {
                const isExpanded = expandedDasha === period.planet || (!expandedDasha && period.is_current);
                return (
                  <Accordion
                    key={`antardasha-${period.planet}-${period.start_date}`}
                    expanded={isExpanded}
                    onChange={(_, expanded) => setExpandedDasha(expanded ? period.planet : false)}
                    disableGutters
                    sx={{
                      mb: 1.25,
                      backgroundColor: 'rgba(255,255,255,0.72)',
                      border: '1px solid rgba(123,45,38,0.12)',
                      boxShadow: 'none',
                      '&:before': { display: 'none' },
                    }}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: MAROON }} />}>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.25, width: '100%' }}>
                        <Typography sx={{ fontWeight: 700, color: MAROON }}>{period.planet}</Typography>
                        <Typography sx={{ color: 'text.secondary', fontSize: '0.9rem' }}>{period.planet_telugu}</Typography>
                        <Chip size="small" label={`${formatDisplayDate(period.start_date)} – ${formatDisplayDate(period.end_date)}`} variant="outlined" sx={{ borderColor: 'rgba(123,45,38,0.25)', color: MAROON }} />
                        {period.is_current ? <Chip size="small" label="Current Mahadasha" color="secondary" /> : null}
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <TableContainer sx={{ overflowX: 'auto', mx: { xs: -1, md: 0 } }}>
                        <Table size="small" sx={{ minWidth: 540 }}>
                          <TableHead>
                            <TableRow>
                              {['Planet / గ్రహం', 'Start Date', 'End Date', 'Duration', 'Status'].map((header) => (
                                <TableCell key={header} sx={{ color: 'primary.main', fontWeight: 700, whiteSpace: 'nowrap' }}>
                                  {header}
                                </TableCell>
                              ))}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {period.antardasha.map((subPeriod) => (
                              <TableRow
                                key={`${period.planet}-${subPeriod.planet}-${subPeriod.start_date}`}
                                sx={{
                                  ...tableRowSx,
                                  backgroundColor: subPeriod.is_current ? 'rgba(200,146,42,0.16)' : undefined,
                                }}
                              >
                                <TableCell>
                                  <Typography sx={{ fontWeight: 600, fontSize: '0.85rem' }}>{subPeriod.planet}</Typography>
                                  <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>{subPeriod.planet_telugu}</Typography>
                                </TableCell>
                                <TableCell>{formatDisplayDate(subPeriod.start_date)}</TableCell>
                                <TableCell>{formatDisplayDate(subPeriod.end_date)}</TableCell>
                                <TableCell>{formatDuration(subPeriod.duration_years)}</TableCell>
                                <TableCell>
                                  {subPeriod.is_current ? (
                                    <Chip label="Current Bhukti" size="small" sx={{ backgroundColor: 'rgba(200,146,42,0.22)', color: MAROON, fontWeight: 700 }} />
                                  ) : '—'}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </AccordionDetails>
                  </Accordion>
                );
              })}
            </CardContent>
          </Card>

          <Card sx={cardSx}>
            <CardContent>
              <Typography variant="h4" sx={{ mb: 1 }}>
                Divisional Charts / వర్గ చక్రాలు
              </Typography>
              <Typography variant="body2" sx={{ mb: 2.5 }}>
                Explore Vargas like Navamsa, Dasamsa, and Shastiamsa with a South Indian style layout.
              </Typography>

              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
                {availableDivisionalCharts.map((chartKey) => (
                  <Chip
                    key={chartKey}
                    label={chartKey}
                    clickable
                    variant={activeChartKey === chartKey ? 'filled' : 'outlined'}
                    onClick={() => setSelectedChart(chartKey)}
                    sx={{
                      fontWeight: 700,
                      color: activeChartKey === chartKey ? '#FFF8EF' : MAROON,
                      backgroundColor: activeChartKey === chartKey ? MAROON : 'transparent',
                      borderColor: 'rgba(123,45,38,0.35)',
                      '&:hover': {
                        backgroundColor: activeChartKey === chartKey ? MAROON : 'rgba(123,45,38,0.06)',
                      },
                    }}
                  />
                ))}
              </Box>

              {activeDivisionalChart ? (
                <Box sx={{ display: 'grid', gap: 2.5 }}>
                  <Box sx={{ textAlign: { xs: 'left', md: 'center' } }}>
                    <Typography variant="h5" sx={{ color: MAROON, fontWeight: 700 }}>{activeDivisionalChart.name}</Typography>
                    <Typography sx={{ color: GOLD, fontWeight: 700, fontFamily: "'Noto Sans Telugu', sans-serif" }}>{activeDivisionalChart.name_telugu}</Typography>
                  </Box>

                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1.1fr) minmax(0, 0.9fr)' }, gap: 3, alignItems: 'start' }}>
                    <DivisionalChartGrid
                      chartName={activeDivisionalChart.name}
                      chartNameTelugu={activeDivisionalChart.name_telugu}
                      entries={activeDivisionalChart.planets}
                    />

                    <TableContainer sx={{ overflowX: 'auto' }}>
                      <Table size="small" sx={{ minWidth: 320 }}>
                        <TableHead>
                          <TableRow>
                            {['Planet / గ్రహం', 'Sign / రాశి'].map((header) => (
                              <TableCell key={header} sx={{ color: 'primary.main', fontWeight: 700, whiteSpace: 'nowrap' }}>
                                {header}
                              </TableCell>
                            ))}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {activeDivisionalChart.planets.map((entry) => (
                            <TableRow key={`${activeChartKey}-${entry.name}`} sx={tableRowSx}>
                              <TableCell>
                                <Typography sx={{ fontWeight: 600, fontSize: '0.85rem' }}>{entry.name}</Typography>
                                <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>{entry.name_telugu}</Typography>
                              </TableCell>
                              <TableCell>
                                <Typography sx={{ fontSize: '0.85rem' }}>{entry.sign}</Typography>
                                <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>{entry.sign_telugu}</Typography>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                </Box>
              ) : (
                <Alert severity="info">Divisional chart data is not available for this Kundali yet.</Alert>
              )}
            </CardContent>
          </Card>

          <Card sx={cardSx}>
            <CardContent>
              <Typography variant="h4" sx={{ mb: 1 }}>
                Planetary Strength / షడ్బల (Shadbala)
              </Typography>
              <Typography variant="body2" sx={{ mb: 3 }}>
                Total planetary strength in Shashtiamsa — consult an astrologer for detailed interpretation.
              </Typography>

              <Box sx={{ display: 'grid', gap: 1.5, mb: 3 }}>
                {shadbalaRows.map((entry) => {
                  const barWidth = `${Math.max((entry.total / maxShadbalaValue) * 100, 4)}%`;

                  return (
                    <Box key={`shadbala-${entry.planet}`} sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '120px minmax(0, 1fr)' }, gap: 1.25, alignItems: 'center' }}>
                      <Box>
                        <Typography sx={{ fontWeight: 700, color: MAROON }}>{entry.planet}</Typography>
                        <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>{entry.planet_telugu}</Typography>
                      </Box>
                      <Box sx={{ position: 'relative', height: 24, borderRadius: 999, backgroundColor: 'rgba(123,45,38,0.08)', overflow: 'hidden' }}>
                        <Box
                          sx={{
                            width: barWidth,
                            height: '100%',
                            background: `linear-gradient(90deg, ${MAROON}, ${GOLD})`,
                            borderRadius: 999,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-end',
                            pr: 1,
                            color: '#FFF',
                            fontWeight: 700,
                            fontSize: '0.8rem',
                          }}
                        >
                          {entry.total.toFixed(2)}
                        </Box>
                      </Box>
                    </Box>
                  );
                })}
              </Box>

              <TableContainer sx={{ overflowX: 'auto', mx: { xs: -1, md: 0 } }}>
                <Table size="small" sx={{ minWidth: 600 }}>
                  <TableHead>
                    <TableRow>
                      {['Planet', 'Sthana', 'Dig', 'Naisargika', 'Chesta', 'Drik', 'Kala', 'Total'].map((header) => (
                        <TableCell key={header} sx={{ color: 'primary.main', fontWeight: 700, whiteSpace: 'nowrap' }}>
                          {header}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {shadbalaRows.map((entry) => (
                      <TableRow key={`shadbala-table-${entry.planet}`} sx={tableRowSx}>
                        <TableCell>
                          <Typography sx={{ fontWeight: 600, fontSize: '0.85rem' }}>{entry.planet}</Typography>
                          <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>{entry.planet_telugu}</Typography>
                        </TableCell>
                        <TableCell>{formatShadbalaValue(entry.sthana_bala)}</TableCell>
                        <TableCell>{formatShadbalaValue(entry.dig_bala)}</TableCell>
                        <TableCell>{formatShadbalaValue(entry.naisargika_bala)}</TableCell>
                        <TableCell>{formatShadbalaValue(entry.chesta_bala)}</TableCell>
                        <TableCell>{formatShadbalaValue(entry.drik_bala)}</TableCell>
                        <TableCell>{formatShadbalaValue(entry.kala_bala)}</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>{formatShadbalaValue(entry.total)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>

          <Card sx={cardSx}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: { xs: 'flex-start', md: 'center' }, justifyContent: 'space-between', gap: 2, flexDirection: { xs: 'column', md: 'row' } }}>
                <Box>
                  <Typography variant="h4" sx={{ mb: 0.75 }}>
                    Next Steps
                  </Typography>
                  <Typography variant="body1">
                    Schedule a consultation with our astrologer for personalized guidance.
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', flexDirection: { xs: 'column', sm: 'row' } }}>
                  <Button variant="outlined" startIcon={<EventAvailableIcon />} onClick={() => navigate('/book-slot')}>
                    Book Consultation
                  </Button>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>
      )}
    </Container>
  );
};

export default Kundali;
